import { DatePicker, Input, Select, Space, Switch, Tag, Tooltip, Typography } from "antd";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import type { FieldConfig } from "./DescriptionsFields";
import PhoneInput, { formatPhone, phoneRule } from "./PhoneInput";
import {
  getCompanyAssets,
  getCompanyDepartments,
  getCompanyUserRoles,
  getCountries,
  getLanguages,
} from "../repository/data.repository";
import { QueryOperation } from "../code/search-query/search-query";

interface RemoteOption {
  value: string;
  label: string;
}

/** A Select that lazy-loads its options once (countries / languages), seeded with the current
 *  selection so the existing value's label shows immediately. Binds to the form via value/onChange. */
function RemoteSelect(props: {
  value?: string;
  onChange?: (value: string) => void;
  load: () => Promise<RemoteOption[]>;
  initialOption?: RemoteOption;
}): JSX.Element {
  const [options, setOptions] = useState<RemoteOption[]>(
    props.initialOption ? [props.initialOption] : [],
  );
  useEffect(() => {
    props.load().then(setOptions).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Select
      size="small"
      showSearch
      allowClear
      optionFilterProp="label"
      className="w-full"
      value={props.value}
      onChange={props.onChange}
      options={options}
    />
  );
}

const loadCountryOptions = (): Promise<RemoteOption[]> =>
  getCountries({ searchQuery: { conditions: [] }, pageNumber: 0, pageSize: 1000 }).then(
    ({ data }) => data.map((c) => ({ value: c.uid, label: c.name })),
  );

const loadLanguageOptions = (): Promise<RemoteOption[]> =>
  getLanguages({ searchQuery: { conditions: [] }, pageNumber: 0, pageSize: 1000 }).then(
    ({ data }) => data.map((l) => ({ value: l.uid, label: l.name })),
  );

// Assets are company-scoped, so the loader needs the user's company.
const loadCompanyAssetOptions =
  (companyUid?: string) => (): Promise<RemoteOption[]> => {
    if (!companyUid) return Promise.resolve([]);
    return getCompanyAssets({
      searchQuery: {
        conditions: [
          { field: "companyUid", operation: QueryOperation.Equal, values: [companyUid] },
        ],
      },
      pageNumber: 0,
      pageSize: 200,
    }).then(({ data }) => data.map((a) => ({ value: a.uid, label: a.name })));
  };

// Departments and roles are company-scoped exactly as assets are, so both loaders need the user's company.
// The backend refuses a department or role belonging to another company, so these pickers are the UI half
// of a rule the API enforces on its own — not the rule itself.
const loadCompanyDepartmentOptions =
  (companyUid?: string) => (): Promise<RemoteOption[]> => {
    if (!companyUid) return Promise.resolve([]);
    return getCompanyDepartments({
      searchQuery: {
        conditions: [
          { field: "companyUid", operation: QueryOperation.Equal, values: [companyUid] },
        ],
      },
      pageNumber: 0,
      pageSize: 200,
    }).then(({ data }) => data.map((d) => ({ value: d.uid, label: d.name })));
  };

const loadCompanyRoleOptions =
  (companyUid?: string) => (): Promise<RemoteOption[]> => {
    if (!companyUid) return Promise.resolve([]);
    return getCompanyUserRoles({
      searchQuery: {
        conditions: [
          { field: "companyUid", operation: QueryOperation.Equal, values: [companyUid] },
        ],
      },
      pageNumber: 0,
      pageSize: 200,
    }).then(({ data }) => data.map((r) => ({ value: r.uid, label: r.name })));
  };

/** The user's Department. Editable combobox, scoped to their company. */
export function companyDepartmentField(
  data: Record<string, any> | null,
  opts: { editable?: boolean } = {},
): FieldConfig {
  return {
    key: "departmentUid",
    label: "Department",
    dataKey: "departmentName",
    editable: opts.editable,
    input: (
      <RemoteSelect
        load={loadCompanyDepartmentOptions(data?.companyUid)}
        initialOption={
          data?.departmentUid
            ? { value: data.departmentUid, label: data.departmentName }
            : undefined
        }
      />
    ),
  };
}

/**
 * The user's company Role — their job position. Editable combobox, scoped to their company.
 *
 * Changing it can MOVE THE PERSON BETWEEN TABS, because Managers and Employees are both defined by the
 * role's IsManager flag. Promoting someone to a management-level role moves them off Employees and onto
 * Managers, and changes which app they sign into. That is what PD-01 §4.2's "role assignment" means.
 *
 * The uid and display name live under different keys depending on which profile is open — the Accounts
 * DTO says companyRoleUid/companyRoleName, the manager and employee DTOs say roleUid/roleName — so both
 * are resolved here rather than at three call sites.
 */
export function companyRoleField(
  data: Record<string, any> | null,
  opts: { editable?: boolean } = {},
): FieldConfig {
  const roleUid = data?.companyRoleUid ?? data?.roleUid;
  const roleName = data?.companyRoleName ?? data?.roleName;
  return {
    key: "roleUid",
    label: "Role",
    dataKey: "companyRoleName",
    editable: opts.editable,
    input: (
      <RemoteSelect
        load={loadCompanyRoleOptions(data?.companyUid)}
        initialOption={roleUid ? { value: roleUid, label: roleName } : undefined}
      />
    ),
    format: () => roleName ?? undefined,
  };
}

/** The user's current Asset (vessel/aircraft/site). Editable combobox (company-scoped) in edit mode;
 *  in read mode the asset name is a quick-link into that asset's crew metrics when `onViewMetrics` is given. */
export function companyAssetField(
  data: Record<string, any> | null,
  opts: {
    editable?: boolean;
    onViewMetrics?: (assetUid: string, assetName?: string) => void;
  } = {},
): FieldConfig {
  return {
    key: "assetUid",
    label: "Asset",
    dataKey: "assetName",
    editable: opts.editable,
    input: (
      <RemoteSelect
        load={loadCompanyAssetOptions(data?.companyUid)}
        initialOption={
          data?.assetUid
            ? { value: data.assetUid, label: data.assetName }
            : undefined
        }
      />
    ),
    format: (value: any) =>
      value && data?.assetUid && opts.onViewMetrics ? (
        <Tooltip title="Drill into this asset's crew metrics">
          <Typography.Link
            onClick={() => opts.onViewMetrics?.(data.assetUid, data.assetName)}
          >
            {value}
          </Typography.Link>
        </Tooltip>
      ) : (
        value ?? undefined
      ),
  };
}

/**
 * Centralized field-builders for user-type profile views (All Users, Employees,
 * Managers, Consultants, Collaborators). They mirror the backend's compositional
 * hierarchy, organized into three tiers — compose a card by stacking the tiers that
 * apply to the user type:
 *
 *   TIER 1 — IDENTITY USER (backend: `ApplicationUser` / IStoreable) — EVERY user.
 *     `userIdentityFields` (name/dob/username), `userContactFields` (personal email/phone),
 *     `userLockoutFields` / `userAccountFields` (lockout + audit). Person-global attributes.
 *
 *   TIER 2 — COMPANY MEMBERSHIP (backend: `CompanyUserProfile` — tenant·company·department·role)
 *     — corporate users only (admin/manager/employee). `companyUserLocalizationFields`
 *     (country/language), companyPhone (currently an opt on `userContactFields`), joinedDate,
 *     internalId/externalId, + the single Organization section the views render inline.
 *     NOTE: `CompanyUserProfile` is the (user × company) MEMBERSHIP row. Today a user has ONE;
 *     the architectural vision is N (a profile per company + a login "directory switcher"),
 *     so role/department/permissions live on the membership, not the user. If that lands,
 *     country/language/gender graduate from the membership up to the person (Tier 1).
 *
 *   TIER 3 — AFFILIATE ASSIGNMENTS (backend: `CompanyCollaborator` join) — consultant/collaborator.
 *     The many-companies "Company Assignments" panel the affiliate views render. No membership tier.
 *
 * Keeping the contact-confirmation rendering in ONE atom (`confirmableContactField`)
 * guarantees the verification feedback is identical across every view — and that the
 * "no value -> no status" rule is enforced in a single place.
 */

const DATE = "DD/MM/YYYY";
const DATETIME = "DD/MM/YYYY HH:mm";

/** Per-field confirmation wiring; lets a view override the backing keys / supply a
 *  click-to-confirm handler (omit `onConfirm` -> the unconfirmed icon is informational). */
export interface ContactConfirmConfig {
  /** Override the key the displayed value is read from (defaults to the field key —
   *  for fields whose display value lives under a differently-named property). */
  dataKey?: string;
  confirmedKey?: string;
  requestedAtKey?: string;
  onConfirm?: () => void;
}

/**
 * A contact field (email / phone) whose value renders with an inline confirmation
 * indicator — but ONLY when a value exists. Three states:
 *   confirmed -> green check · confirmation-requested -> amber clock · otherwise -> red alert
 * The red "unconfirmed" alert is clickable when an `onConfirm` handler is supplied.
 */
export function confirmableContactField(opts: {
  key: string;
  label: string;
  data: Record<string, any> | null;
  confirmedKey: string;
  requestedAtKey?: string;
  dataKey?: string;
  span?: number;
  editable?: boolean;
  onConfirm?: () => void;
  /** Phone field: country-code picker input, E.164 validation and international display. */
  phone?: boolean;
  /** Email field: enforce RFC-style format in edit mode so garbage like "abc" or "qwe@" is rejected
   *  (previously accepted and silently saved, breaking activation + password reset). */
  email?: boolean;
}): FieldConfig {
  const {
    key,
    label,
    data,
    confirmedKey,
    requestedAtKey,
    dataKey,
    span,
    editable,
    onConfirm,
    phone,
    email,
  } = opts;
  return {
    key,
    label,
    span,
    dataKey,
    editable,
    input: editable ? phone ? <PhoneInput size="small" /> : <Input size="small" /> : undefined,
    rules: editable
      ? phone
        ? [phoneRule]
        : email
          ? [{ type: "email", message: "Invalid email format" }]
          : undefined
      : undefined,
    format: (value: any) => (
      <Space>
        <span>{phone ? formatPhone(value) : value}</span>
        {value &&
          (data?.[confirmedKey] ? (
            <CheckCircleOutlined style={{ color: "#52c41a" }} />
          ) : requestedAtKey && data?.[requestedAtKey] ? (
            <ClockCircleOutlined style={{ color: "#faad14" }} />
          ) : onConfirm ? (
            <ExclamationCircleOutlined
              style={{ color: "#ff4d4f", cursor: "pointer" }}
              onClick={onConfirm}
            />
          ) : (
            <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
          ))}
      </Space>
    ),
  };
}

/** Identity layer shared by every user: name parts (editable) + read-only username + date of birth.
 *  DOB is editable by default (admin cards manage it); pass `dateOfBirthEditable: false` for self-service
 *  views like MyProfile where the self endpoint can't update it (read-only). */
export function userIdentityFields(
  opts: { dateOfBirthEditable?: boolean } = {},
): FieldConfig[] {
  const { dateOfBirthEditable = true } = opts;
  return [
    { key: "userName", label: "User Name" },
    {
      key: "firstName",
      label: "First Name",
      editable: true,
      input: <Input size="small" />,
      rules: [{ required: true, message: "First Name is required" }],
    },
    { key: "middleName", label: "Middle Name", editable: true, input: <Input size="small" /> },
    {
      key: "lastName",
      label: "Last Name",
      editable: true,
      input: <Input size="small" />,
      rules: [{ required: true, message: "Last Name is required" }],
    },
    {
      key: "dateOfBirth",
      label: "Date of Birth",
      span: 2,
      editable: dateOfBirthEditable,
      input: dateOfBirthEditable ? (
        // Guard against obviously-nonsense dates — nobody is born in the future, and 1900 is a safe
        // lower bound for a corporate directory (the oldest living person on record is younger than that).
        <DatePicker
          size="small"
          format={DATE}
          className="w-full"
          disabledDate={(current) =>
            !!current && (current.isAfter(dayjs().endOf("day")) || current.isBefore(dayjs("1900-01-01")))
          }
        />
      ) : undefined,
      format: (value: any) => (value ? dayjs(value).format(DATE) : undefined),
    },
  ];
}

/** TIER 1 — Personal contact shared by EVERY user: Email + Phone, each with the centralized
 *  confirmation indicator. Company Phone is NOT here — it is a company-membership field
 *  (see `corporateContactFields`), so this stays strictly person-global. */
export function userContactFields(
  data: Record<string, any> | null,
  opts: {
    editable?: boolean;
    email?: ContactConfirmConfig;
    phone?: ContactConfirmConfig;
  } = {},
): FieldConfig[] {
  const { editable, email, phone } = opts;
  return [
    confirmableContactField({
      key: "email",
      label: "Email",
      data,
      dataKey: email?.dataKey,
      confirmedKey: email?.confirmedKey ?? "emailConfirmed",
      requestedAtKey: email?.requestedAtKey ?? "emailConfirmationRequestedAt",
      editable,
      onConfirm: email?.onConfirm,
      email: true,
    }),
    confirmableContactField({
      key: "phoneNumber",
      label: "Phone Number",
      data,
      dataKey: phone?.dataKey,
      confirmedKey: phone?.confirmedKey ?? "phoneNumberConfirmed",
      requestedAtKey: phone?.requestedAtKey ?? "phoneConfirmationRequestedAt",
      editable,
      onConfirm: phone?.onConfirm,
      phone: true,
    }),
  ];
}

/** TIER 2 — Company-membership contact: Company Phone (lives on `CompanyUserProfile`).
 *  Corporate users (admin/manager/employee) compose this onto `userContactFields`;
 *  affiliates do not have it. */
export function corporateContactFields(
  data: Record<string, any> | null,
  opts: { editable?: boolean; companyPhone?: ContactConfirmConfig } = {},
): FieldConfig[] {
  const { editable, companyPhone } = opts;
  return [
    confirmableContactField({
      key: "companyPhoneNumber",
      label: "Company Phone",
      data,
      dataKey: companyPhone?.dataKey,
      confirmedKey: companyPhone?.confirmedKey ?? "companyPhoneNumberConfirmed",
      requestedAtKey: companyPhone?.requestedAtKey ?? "companyPhoneConfirmationRequestedAt",
      editable,
      onConfirm: companyPhone?.onConfirm,
      phone: true,
    }),
  ];
}

/** Lockout layer: the LockoutEnabled policy flag (editable toggle) + current lock expiry.
 *  Distinct from the Lock/Unlock action (which sets the expiry); this is the policy flag. */
export function userLockoutFields(opts: { editable?: boolean } = {}): FieldConfig[] {
  const { editable } = opts;
  const dt = (value: any) => (value ? dayjs(value).format(DATETIME) : undefined);
  return [
    {
      key: "lockoutEnabled",
      label: "Lockout Enabled",
      editable,
      valuePropName: "checked",
      input: (
        <Switch size="small" checkedChildren="Enabled" unCheckedChildren="Disabled" />
      ),
      format: (value: any) =>
        value === undefined || value === null ? undefined : (
          <Tag color={value ? "orange" : "green"}>{value ? "Enabled" : "Disabled"}</Tag>
        ),
    },
    { key: "lockoutEnd", label: "Locked Until", format: dt },
  ];
}

/** Account / audit layer shared by every user: lockout state + audit timestamps. Pass
 *  `editable: true` to make Lockout Enabled a toggle in edit mode; `joinedDate: false`
 *  for users with no profile (affiliates) where there is no JoinedDate to show. */
export function userAccountFields(
  opts: { joinedDate?: boolean; editable?: boolean } = {},
): FieldConfig[] {
  const { joinedDate = true, editable } = opts;
  const dt = (value: any) => (value ? dayjs(value).format(DATETIME) : undefined);
  const fields: FieldConfig[] = [
    ...userLockoutFields({ editable }),
    { key: "createdOn", label: "Created On", format: dt },
  ];
  if (joinedDate) {
    fields.push({ key: "joinedDate", label: "Joined Date", format: dt });
  }
  return fields;
}

/** Affiliate (Consultant / Collaborator) professional details — client brief 2026-11-03.
 *  Four free-text fields + a comma-separated languages list. A proper many-to-many against the
 *  Languages table waits for client confirmation that it needs to be structured. */
export function affiliateProfessionalFields(
  _data: Record<string, any> | null,
  opts: { editable?: boolean } = {},
): FieldConfig[] {
  const { editable } = opts;
  return [
    { key: "professionalTitle", label: "Professional Title", editable },
    { key: "qualifications", label: "Qualifications", editable },
    { key: "specialisation", label: "Specialisation", editable },
    {
      key: "languagesOffered",
      label: "Languages Offered",
      editable,
    },
    {
      key: "bio",
      label: "Bio",
      editable,
      span: 2,
      input: <Input.TextArea rows={3} size="small" />,
    },
  ];
}

/** Affiliate services offered — service type, delivery mode (Online / InPerson / Both),
 *  availability free-text, time zone. */
export function affiliateServicesFields(
  _data: Record<string, any> | null,
  opts: { editable?: boolean } = {},
): FieldConfig[] {
  const { editable } = opts;
  return [
    { key: "serviceType", label: "Service Type", editable },
    {
      key: "deliveryMode",
      label: "Delivery Mode",
      editable,
      input: (
        <Select
          size="small"
          options={[
            { value: "Online", label: "Online" },
            { value: "InPerson", label: "In person" },
            { value: "Both", label: "Both" },
          ]}
        />
      ),
    },
    { key: "availability", label: "Availability", editable },
    { key: "timeZone", label: "Time Zone", editable },
  ];
}

/** Affiliate agreements block — service agreement, NDA, DPA. Each tracks the acceptance
 *  timestamp (read-only, stamped by the affiliate when they accepted) and the document
 *  version they signed, so a re-sign is triggered when the version bumps. */
export function affiliateAgreementsFields(
  _data: Record<string, any> | null,
  opts: { editable?: boolean } = {},
): FieldConfig[] {
  const { editable } = opts;
  const dt = (value: any) => (value ? dayjs(value).format(DATETIME) : undefined);
  return [
    { key: "serviceAgreementVersion", label: "Service Agreement — Version", editable },
    {
      key: "serviceAgreementAcceptedAt",
      label: "Service Agreement — Accepted",
      format: dt,
    },
    { key: "ndaVersion", label: "NDA — Version", editable },
    { key: "ndaAcceptedAt", label: "NDA — Accepted", format: dt },
    { key: "dpaVersion", label: "DPA — Version", editable },
    { key: "dpaAcceptedAt", label: "DPA — Accepted", format: dt },
  ];
}

/** Affiliate access block — permission group + optional access expiry (for temporary
 *  consultants whose contract ends on a specific date). */
export function affiliateAccessFields(
  _data: Record<string, any> | null,
  opts: { editable?: boolean } = {},
): FieldConfig[] {
  const { editable } = opts;
  const dt = (value: any) => (value ? dayjs(value).format(DATETIME) : undefined);
  return [
    { key: "permissionGroup", label: "Permission Group", editable },
    {
      key: "accessExpiresAt",
      label: "Access Expires On",
      format: dt,
      editable,
      input: <DatePicker size="small" style={{ width: "100%" }} />,
    },
  ];
}

/** Localization layer for users that have a CompanyUserProfile (managers, employees, and the
 *  company-scoped All Users) — country of origin + preferred/known (test) language. Editable
 *  comboboxes in edit mode; read mode shows the resolved names. Not applicable to affiliates. */
export function companyUserLocalizationFields(
  data: Record<string, any> | null,
  opts: { editable?: boolean } = {},
): FieldConfig[] {
  const { editable } = opts;
  return [
    {
      key: "countryUid",
      label: "Country",
      dataKey: "countryName",
      editable,
      input: (
        <RemoteSelect
          load={loadCountryOptions}
          initialOption={
            data?.countryUid
              ? { value: data.countryUid, label: data.countryName }
              : undefined
          }
        />
      ),
    },
    {
      key: "testLanguageUid",
      label: "Language",
      dataKey: "testLanguageName",
      editable,
      input: (
        <RemoteSelect
          load={loadLanguageOptions}
          initialOption={
            data?.testLanguageUid
              ? { value: data.testLanguageUid, label: data.testLanguageName }
              : undefined
          }
        />
      ),
    },
  ];
}
